import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import {
  resolveApiAssetUrl,
  type CompanyContext,
  type MentionSummary
} from "@/shared/api/api-client";
import { AppLink } from "@/shared/components/AppLink";
import { paletteFaceIcon } from "@/shared/components/SentimentBadge";
import { polarityPalette } from "@/shared/types/polarity";
import type { Mention } from "@/shared/types/mention";
import { formatInteger } from "@/shared/utils/format";
import { HeroCompanyPicker } from "@/features/companies/HeroCompanyPicker";
import { SummaryPeriodPicker } from "@/shared/components/SummaryPeriodPicker";
import { todayInInstallation } from "@/shared/utils/installation-clock";
import {
  summaryWindowShort,
  summaryWindowText,
  type SummaryPeriod
} from "@/shared/utils/summary-window";

type HomeHeroProps = {
  dayLabel: string;
  companyCount: number;
  /** O resumo é de UMA empresa (a última visitada). Sem ele, o hero é só
   * a saudação — nunca número chutado. */
  summary: MentionSummary | null;
  company: CompanyContext | null;
  /** As menções mais recentes da empresa, pro feed. Vazio: o hero fica só com
   * o texto. */
  mentions: Mention[];
  /** Destinos dos botões do hero e dos cartões do feed. Links, e não
   * callbacks (2026-10-26): valem "abrir em nova aba". */
  dashboardHref: string;
  mentionsHref: string;
  /** As empresas que dá pra fixar no painel, a fixada (nulo = automático) e
   * a troca. Sem `onChooseCompany`, o selo é só leitura. */
  companies?: CompanyContext[];
  fixedCompanyId?: string | null;
  onChooseCompany?: (companyId: string | null) => void;
  /** O período dos números (1, 7, 15 ou 30 dias até o último dado), o
   *  mesmo da Visão geral. Sem `onDaysChange`, não há seletor. */
  days?: SummaryPeriod;
  onDaysChange?: (days: SummaryPeriod) => void;
};

function reducedMotion(): boolean {
  return typeof window !== "undefined" && (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false);
}

/** Conta de 0 até o valor, uma vez por valor. Quem pede menos movimento
 * recebe o número final na hora. */
function useCountUp(target: number, duration = 1200): number {
  const [value, setValue] = useState(() => (reducedMotion() ? target : 0));
  useEffect(() => {
    if (reducedMotion()) {
      setValue(target);
      return;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      setValue(Math.round(target * (1 - Math.pow(1 - progress, 3))));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);
  return value;
}

/**
 * O topo da Home (2026-10-15): a frase que o resumo da empresa permite dizer
 * — que fatia das menções classificadas é positiva —, o que pede revisão logo
 * embaixo e os dois botões. Os números são os do mesmo resumo que alimenta os
 * cartões. À direita, as menções mais recentes da empresa num mosaico de
 * vidro que troca devagar (ver `HeroFeed`). O hero não tem moldura: fica
 * direto sobre o fundo animado da Home (`home-backdrop`, no `CompanyHub`). Todo
 * movimento (contagem, mosaico) é lento e some com
 * `prefers-reduced-motion`.
 */
export function HomeHero({
  dayLabel,
  companyCount,
  summary,
  company,
  mentions,
  dashboardHref,
  mentionsHref,
  companies = [],
  fixedCompanyId = null,
  onChooseCompany,
  days,
  onDaysChange
}: HomeHeroProps) {
  const hasSummary = summary !== null && company !== null;
  const hasFeed = hasSummary && mentions.length > 0;
  return (
    <header className={`home-hero${hasSummary ? "" : " home-hero--plain"}${hasFeed ? "" : " home-hero--solo"}`}>
      <div className="home-hero-copy">
        {/* Com o panorama, a linha do dia sai: o hero abre direto na empresa.
            Sem ele, é ela que sobra. */}
        {!hasSummary && (
          <span className="eyebrow">
            {dayLabel}
            {dayLabel ? " · " : ""}
            {companyCount} {companyCount === 1 ? "empresa" : "empresas"} sob gestão
          </span>
        )}
        {hasSummary && (
          <HeroPanorama
            summary={summary}
            company={company}
            dashboardHref={dashboardHref}
            mentionsHref={mentionsHref}
            picker={onChooseCompany ? { companies, fixedId: fixedCompanyId, onChoose: onChooseCompany } : null}
            period={days !== undefined && onDaysChange ? { days, onChange: onDaysChange } : null}
          />
        )}
      </div>
      {hasFeed && <HeroFeed key={mentions.length} mentions={mentions} href={mentionsHref} />}
    </header>
  );
}

type Share = { classified: number; favorable: number; percent: number };

/** A fatia favorável sobre as CLASSIFICADAS (a soma da própria lista),
 * nunca sobre o total da janela: menção sem polaridade não é neutra, é
 * "ainda sem classificação". Favorável = o balde `positive` (Positivo e
 * Favorável), o mesmo que `positive_count` soma. */
function favorableShare(summary: MentionSummary): Share {
  const classified = summary.polarity_counts.reduce((sum, entry) => sum + entry.count, 0);
  const favorable = summary.polarity_counts
    .filter(entry => entry.tone === "positive")
    .reduce((sum, entry) => sum + entry.count, 0);
  return { classified, favorable, percent: classified ? Math.round((favorable / classified) * 100) : 0 };
}

function HeroPanorama({
  summary,
  company,
  dashboardHref,
  mentionsHref,
  picker,
  period
}: {
  summary: MentionSummary;
  company: CompanyContext;
  dashboardHref: string;
  mentionsHref: string;
  picker: { companies: CompanyContext[]; fixedId: string | null; onChoose: (companyId: string | null) => void } | null;
  period: { days: SummaryPeriod; onChange: (days: SummaryPeriod) => void } | null;
}) {
  const { classified, percent } = favorableShare(summary);
  const shownPercent = useCountUp(percent);
  const wording = company.polarity_scale === "four_level" ? "positivas ou favoráveis" : "positivas";
  const negative = summary.negative_count;
  const pending = summary.unclassified_count;
  const attention = [
    negative > 0 ? `${formatInteger(negative)} ${negative === 1 ? "negativa" : "negativas"}` : null,
    pending > 0 ? `${formatInteger(pending)} sem classificação` : null
  ].filter((part): part is string => part !== null);
  const sentence = classified
    ? `${percent}% das menções classificadas de ${company.name} são ${wording}.`
    : `Ainda não há menções classificadas em ${company.name}.`;

  return (
    <>
      {picker ? (
        <HeroCompanyPicker
          company={company}
          windowLabel={summaryWindowShort(summary)}
          companies={picker.companies}
          fixedId={picker.fixedId}
          onChoose={picker.onChoose}
        />
      ) : (
        <div className="home-hero-company">
          <span className={`home-hero-mark${company.logo_url ? " has-logo" : ""}`}>
            {company.logo_url ? (
              <img src={resolveApiAssetUrl(company.logo_url)} alt="" />
            ) : (
              company.name.slice(0, 2).toUpperCase()
            )}
          </span>
          <div>
            <strong>{company.name}</strong>
            <span>{summaryWindowShort(summary)}</span>
          </div>
        </div>
      )}

      {/* O leitor de tela ouve a frase inteira e pronta, não a contagem. */}
      <h2 className="home-hero-headline" aria-label={sentence}>
        {classified ? (
          <>
            <span className="home-hero-pct" aria-hidden="true">{shownPercent}%</span>
            <span aria-hidden="true"> das menções classificadas de {company.name} são {wording}.</span>
          </>
        ) : (
          <span aria-hidden="true">{sentence}</span>
        )}
      </h2>

      <p className="home-hero-note">
        {classified
          ? `Panorama sobre ${formatInteger(classified)} menções classificadas.`
          : "Envie uma planilha ou classifique as menções para ver o panorama."}
        {attention.length > 0 ? ` ${attention.join(" e ")} pedem revisão.` : classified ? " Nada pendente de revisão." : ""}
      </p>

      {/* O período e de que dias são os números: a janela termina no
          ÚLTIMO DADO, não hoje (2026-10-19), e a frase diz isso. */}
      <div className="home-hero-period">
        {period && <SummaryPeriodPicker days={period.days} onChange={period.onChange} />}
        <p>{summaryWindowText(summary, todayInInstallation())}</p>
      </div>

      <div className="home-hero-actions">
        <AppLink className="primary-button" to={mentionsHref}>
          Abrir menções <ArrowRight size={15} aria-hidden="true" />
        </AppLink>
        <AppLink className="secondary-button" to={dashboardHref}>
          Ver dashboard
        </AppLink>
      </div>
    </>
  );
}

/** Quantas menções o mosaico mostra de uma vez, quantas entram no rodízio e de
 * quanto em quanto tempo um ladrilho troca. */
const MOSAIC_TILES = 4;
const MOSAIC_POOL = 12;
const MOSAIC_INTERVAL_MS = 4500;
/** Quanto dura a troca (a saída da menção antiga), igual ao CSS. */
const MOSAIC_SWAP_MS = 900;
/** A ordem em que os ladrilhos trocam: os menores primeiro, o grande por último. */
const MOSAIC_TURNS = [1, 2, 3, 0];

/**
 * As menções mais recentes num mosaico de vidro: um ladrilho grande, dois
 * pequenos ao lado e um largo embaixo. De tempos em tempos UM ladrilho troca de
 * menção (a próxima da fila que não está na tela), com um esmaecer suave — um
 * movimento por vez. Passar o mouse ou focar pausa; quem pede menos movimento não
 * vê a troca. Clicar leva à tela de menções.
 */
function HeroFeed({ mentions, href }: { mentions: Mention[]; href: string }) {
  const pool = mentions.slice(0, MOSAIC_POOL);
  const tiles = Math.min(MOSAIC_TILES, pool.length);
  // `leaving` guarda a menção que acabou de sair de um ladrilho, pra ela sair
  // animada por cima da que entra.
  const [mosaic, setMosaic] = useState(() => ({
    shown: Array.from({ length: tiles }, (_, index) => index),
    turn: 0,
    leaving: null as { slot: number; index: number } | null
  }));
  const [paused, setPaused] = useState(false);
  const rotates = pool.length > tiles && !reducedMotion();
  const { shown, turn, leaving } = mosaic;
  useEffect(() => {
    if (!rotates || paused) return;
    const timer = window.setTimeout(() => {
      setMosaic(current => {
        const order = MOSAIC_TURNS.filter(slot => slot < current.shown.length);
        const slot = order[current.turn % order.length];
        // a próxima da fila depois da que está naquele ladrilho, pulando as que já
        // estão na tela
        let next = current.shown[slot];
        do next = (next + 1) % pool.length;
        while (current.shown.includes(next));
        return {
          shown: current.shown.map((value, index) => (index === slot ? next : value)),
          turn: current.turn + 1,
          leaving: { slot, index: current.shown[slot] }
        };
      });
    }, MOSAIC_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [rotates, paused, turn, pool.length]);
  // a que saiu some do DOM quando a animação de saída termina
  useEffect(() => {
    if (!leaving) return;
    const timer = window.setTimeout(() => setMosaic(current => ({ ...current, leaving: null })), MOSAIC_SWAP_MS);
    return () => window.clearTimeout(timer);
  }, [leaving]);
  return (
    <section
      className="home-feed"
      aria-label="Menções mais recentes"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <ul className="home-feed-mosaic" data-count={tiles}>
        {shown.map((poolIndex, slot) => {
          const mention = pool[poolIndex];
          const palette = polarityPalette(mention.polarity);
          const Icon = paletteFaceIcon[palette];
          const untitled = mention.title === "Publicação sem título";
          return (
            <li key={slot} className="home-feed-tile" data-slot={slot}>
              {/* A chave pela menção remonta o cartão na troca: é o que dispara o
                  esmaecer de entrada. */}
              {leaving?.slot === slot && (
                <FeedCard key={`leaving-${pool[leaving.index].id}`} mention={pool[leaving.index]} big={slot === 0} leaving />
              )}
              <AppLink key={mention.id} className={`home-feed-card ${palette}`} to={href}>
                <span className="home-feed-head">
                  <span className="home-feed-face" role="img" aria-label={mention.sentiment}>
                    <Icon size={16} aria-hidden="true" />
                  </span>
                  <small>{mention.source} · {mention.date}</small>
                </span>
                <strong>{untitled ? mention.excerpt : mention.title}</strong>
                {slot === 0 && !untitled && mention.excerpt && <span className="home-feed-excerpt">{mention.excerpt}</span>}
              </AppLink>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** A menção que sai de um ladrilho na troca: só visual, por cima da que entra. */
function FeedCard({ mention, big, leaving }: { mention: Mention; big: boolean; leaving: boolean }) {
  const palette = polarityPalette(mention.polarity);
  const Icon = paletteFaceIcon[palette];
  const untitled = mention.title === "Publicação sem título";
  return (
    <span className={`home-feed-card ${palette}${leaving ? " is-leaving" : ""}`} aria-hidden="true">
      <span className="home-feed-head">
        <span className="home-feed-face">
          <Icon size={16} aria-hidden="true" />
        </span>
        <small>{mention.source} · {mention.date}</small>
      </span>
      <strong>{untitled ? mention.excerpt : mention.title}</strong>
      {big && !untitled && mention.excerpt && <span className="home-feed-excerpt">{mention.excerpt}</span>}
    </span>
  );
}
